import React from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { S2Checkbox } from 'apps/sigma2-ui/src/components/ui/Checkbox';
import { DatabaseScheduleRetryableConfig } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleRetryableEnabledConfig } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleRetryInterval } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule';
import { DatabaseScheduleRetryableEnableFor } from 'libs/end-to-end/src/types/databases/scheduling/database-schedule-retryable-enable-for';

const RETRY_INTERVAL_UNITS: {
  value: DatabaseScheduleRetryInterval['unit'];
  label: string;
}[] = [
  { value: 'minutes', label: 'Minutes' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
];

const ENABLE_FOR_OPTIONS: {
  value: DatabaseScheduleRetryableEnableFor;
  label: string;
}[] = [
  { value: 'datasets', label: 'Datasets' },
  { value: 'transforms', label: 'Transforms' },
  { value: 'post-tlp-flows', label: 'Post TLP flows' },
];

const DEFAULT_ENABLED_CONFIG: DatabaseScheduleRetryableEnabledConfig = {
  type: 'enabled',
  maxRetryAttempts: 3,
  retryInterval: { value: 5, unit: 'minutes' },
  enableFor: ['datasets', 'transforms', 'post-tlp-flows'],
  notifyOnFailure: false,
};

export const DatabaseScheduleRetryableConfigEditor = ({
  retryableConfig,
  setRetryableConfig,
}: {
  retryableConfig: DatabaseScheduleRetryableConfig;
  setRetryableConfig: (config: DatabaseScheduleRetryableConfig) => void;
}) => {
  const isEnabled = retryableConfig.type === 'enabled';
  const enabledConfig = isEnabled ? retryableConfig : null;

  const setEnabled = (enabled: boolean) => {
    if (enabled) {
      setRetryableConfig(DEFAULT_ENABLED_CONFIG);
    } else {
      setRetryableConfig({ type: 'disabled' });
    }
  };

  const updateEnabledConfig = (
    partial: Partial<DatabaseScheduleRetryableEnabledConfig>,
  ) => {
    if (!isEnabled || !enabledConfig) return;
    setRetryableConfig({ ...enabledConfig, ...partial });
  };

  const toggleEnableFor = (value: DatabaseScheduleRetryableEnableFor) => {
    if (!enabledConfig) return;
    const current = enabledConfig.enableFor;
    const next = current.includes(value)
      ? current.filter((x) => x !== value)
      : [...current, value];
    updateEnabledConfig({ enableFor: next });
  };

  return (
    <Spacing size="md" direction="vertical">
      <S2Typography variant="headline-5" translatedContent="Retry on failure" />
      <S2Typography
        variant="body-6"
        translatedContent="When enabled, failed schedule runs can be retried automatically for selected stages (datasets, transforms, post-TLP flows)."
      />
      <S2Checkbox checked={isEnabled} onChange={() => setEnabled(!isEnabled)}>
        Enable retry on failure
      </S2Checkbox>

      {isEnabled && enabledConfig && (
        <Spacing
          size="sm"
          direction="vertical"
          className="ml-6 border-l-2 border-gray-200 pl-4"
        >
          <Spacing size="xs" direction="vertical">
            <label className="block text-sm font-medium text-gray-700">
              Max retry attempts
            </label>
            <input
              type="number"
              min={1}
              max={99}
              value={enabledConfig.maxRetryAttempts}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                if (!Number.isNaN(n) && n >= 1) {
                  updateEnabledConfig({ maxRetryAttempts: n });
                }
              }}
              className="form-input w-24 rounded border border-gray-300 px-2 py-1 text-sm"
              aria-label="Max retry attempts"
            />
          </Spacing>

          <Spacing size="xs" direction="vertical">
            <label className="block text-sm font-medium text-gray-700">
              Retry interval
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={enabledConfig.retryInterval.value}
                onChange={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (!Number.isNaN(n) && n >= 1) {
                    updateEnabledConfig({
                      retryInterval: {
                        ...enabledConfig.retryInterval,
                        value: n,
                      },
                    });
                  }
                }}
                className="form-input w-20 rounded border border-gray-300 px-2 py-1 text-sm"
                aria-label="Retry interval value"
              />
              <select
                value={enabledConfig.retryInterval.unit}
                onChange={(e) => {
                  updateEnabledConfig({
                    retryInterval: {
                      ...enabledConfig.retryInterval,
                      unit: e.target
                        .value as DatabaseScheduleRetryInterval['unit'],
                    },
                  });
                }}
                className="form-select rounded border border-gray-300 px-2 py-1 text-sm"
                aria-label="Retry interval unit"
              >
                {RETRY_INTERVAL_UNITS.map((u) => (
                  <option key={u.value} value={u.value}>
                    {u.label}
                  </option>
                ))}
              </select>
            </div>
          </Spacing>

          <Spacing size="xs" direction="vertical">
            <span className="block text-sm font-medium text-gray-700">
              Retry for stages
            </span>
            <div className="flex flex-wrap gap-4">
              {ENABLE_FOR_OPTIONS.map((opt) => (
                <S2Checkbox
                  key={opt.value}
                  checked={enabledConfig.enableFor.includes(opt.value)}
                  onChange={() => toggleEnableFor(opt.value)}
                >
                  {opt.label}
                </S2Checkbox>
              ))}
            </div>
          </Spacing>

          <S2Checkbox
            checked={enabledConfig.notifyOnFailure}
            onChange={() => {
              updateEnabledConfig({
                notifyOnFailure: !enabledConfig.notifyOnFailure,
              });
            }}
          >
            Notify on failure
          </S2Checkbox>
        </Spacing>
      )}
    </Spacing>
  );
};
